/**
 * Phase « courante » d’un cas pour la liste des conceptions + filtre aligné.
 */
import { normalizeCasePhasesList } from './caseWorkflowProgress';

const sortByGroupThenOrder = (a, b, orderDir = 1) => {
  const ga = String(a.group ?? '').localeCompare(String(b.group ?? ''));
  if (ga !== 0) return ga;
  return orderDir * ((a.phase_order ?? 0) - (b.phase_order ?? 0));
};

/**
 * Phase à afficher : en cours > prochaine en attente > dernière terminée.
 * @param {Array<{ phase_name?: string, status?: string, group?: string|number, phase_order?: number, phase_template_id?: string }>} phases
 */
export function getCaseDisplayTimelinePhase(phases = []) {
  const list = normalizeCasePhasesList(phases);
  if (!list.length) return null;

  const inProgress = list.filter((p) => p.status === 'in_progress');
  if (inProgress.length) {
    return [...inProgress].sort((a, b) => sortByGroupThenOrder(a, b, -1))[0];
  }

  const pending = phases.filter((p) => p.status === 'pending');
  if (pending.length) {
    return [...pending].sort((a, b) => sortByGroupThenOrder(a, b, 1))[0];
  }

  const completed = phases.filter((p) => p.status === 'completed');
  if (completed.length) {
    return [...completed].sort((a, b) => sortByGroupThenOrder(a, b, -1))[0];
  }

  return phases[0];
}

/**
 * @param {{ phaseRaw?: string, timelinePhases?: Array<{ phase_name?: string, status?: string }> }} caseRow
 * @param {string} phaseFilterValue — phase_name du template
 */
export function caseMatchesPhaseFilter(caseRow, phaseFilterValue) {
  if (!phaseFilterValue) return true;

  const phases = normalizeCasePhasesList(caseRow?.timelinePhases);
  const hasInProgress = phases.some(
    (p) => p.status === 'in_progress' && p.phase_name === phaseFilterValue
  );
  if (hasInProgress) return true;

  return caseRow?.phaseRaw === phaseFilterValue;
}

const GROUP_LABELS = {
  '1': 'Conception',
  '2': 'Production',
};

/**
 * Options de filtre depuis phase_templates (référentiel), pas l’historique des cas.
 * @param {Array<{ id: string, phase_name: string, group?: string|number, phase_order?: number }>} templates
 */
export function buildPhaseFilterOptionsFromTemplates(templates = []) {
  const sorted = [...templates].sort((a, b) => sortByGroupThenOrder(a, b, 1));

  const nameCounts = sorted.reduce((acc, t) => {
    const n = t.phase_name || '';
    acc[n] = (acc[n] || 0) + 1;
    return acc;
  }, {});

  const options = [{ value: '', label: 'Toutes les phases' }];

  for (const tpl of sorted) {
    const name = tpl.phase_name?.trim();
    if (!name) continue;

    const dup = nameCounts[name] > 1;
    const groupKey = String(tpl.group ?? '');
    const prefix = GROUP_LABELS[groupKey] || (groupKey ? `Groupe ${groupKey}` : null);
    const label = dup && prefix ? `${prefix} — ${name}` : name;

    if (!options.some((o) => o.value === name)) {
      options.push({ value: name, label });
    }
  }

  return options;
}

/**
 * Phase « Révision initiale » (G1/P2) pour filtre agent administratif.
 * @param {Array<{ group?: string|number, phase_order?: number, responsible_user_id?: string }>} phases
 */
export function getInitialReviewPhaseFromList(phases = []) {
  const list = normalizeCasePhasesList(phases);
  return (
    list.find((p) => String(p.group) === '1' && Number(p.phase_order) === 2) ?? null
  );
}

/** UUID responsable révision initiale, ou null si non assigné. */
export function getInitialReviewAgentId(phases = []) {
  return getInitialReviewPhaseFromList(phases)?.responsible_user_id ?? null;
}
